/**
 * GraphQL Queries and Mutations for Inspections
 * Mirrors the REST API functions from evirApi.js
 * Uses the existing clientQuery/clientMutation pattern for proper auth handling
 */

import { clientQuery, clientMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

const buildInspectionSearchConditions = (params) => {
	const {
		startTime,
		endTime,
		dates,
		assetIds,
		assetTagIds,
		inspectorIds,
		inspectionTypeIds,
		inspectionNameIds,
		severities,
		verification,
		zoneLayoutIds,
		inspectionIds,
	} = params;

	const conditions = [];
	const effectiveStartTime = startTime || dates?.[0]?.startTime;
	const effectiveEndTime = endTime || dates?.[0]?.endTime;

	if (effectiveStartTime && effectiveEndTime) {
		conditions.push({
			range: {
				fields: ['startTime'],
				GTE: effectiveStartTime,
				LTE: effectiveEndTime,
			},
		});
	}

	if (assetIds && assetIds.length > 0) {
		conditions.push({
			fields: ['asset_ids'],
			terms: assetIds,
			exact: true,
		});
	}

	if (assetTagIds && assetTagIds.length > 0) {
		conditions.push({
			fields: ['asset_tagIds'],
			terms: assetTagIds.map(String),
			exact: true,
		});
	}

	if (zoneLayoutIds && zoneLayoutIds.length > 0) {
		conditions.push({
			fields: ['inspectionLayoutId'],
			terms: zoneLayoutIds,
			exact: true,
		});
	}

	if (inspectorIds && inspectorIds.length > 0) {
		conditions.push({
			fields: ['driverId'],
			terms: inspectorIds,
			exact: true,
		});
	}

	if (inspectionTypeIds && inspectionTypeIds.length > 0) {
		conditions.push({
			fields: ['categoryId'],
			terms: inspectionTypeIds,
			exact: true,
		});
	}

	if (inspectionNameIds && inspectionNameIds.length > 0) {
		conditions.push({
			fields: ['typeId'],
			terms: inspectionNameIds,
			exact: true,
		});
	}

	if (severities && severities.length > 0) {
		conditions.push({
			fields: ['maxSeverity'],
			terms: severities,
			exact: true,
		});
	}

	if (verification && verification.length > 0) {
		verification.forEach((item) => {
			conditions.push({
				fields: [item],
				terms: ['true'],
				exact: true,
			});
		});
	}

	if (inspectionIds && inspectionIds.length > 0) {
		conditions.push({
			fields: ['id'],
			terms: inspectionIds.map(String),
			exact: true,
		});
	}

	return conditions;
};

/**
 * Get inspection types for a company
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Array>} List of inspection types
 */
export const getInspectionTypes = async () => {
	const query = `
		query GetInspectionTypes($sort: [InspectionTypeSortInput]) {
			searchInspectionTypes(sort: $sort) {
				items {
					id
					name
					draft
					createdOn
					createdBy
					updatedOn
					updatedBy
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{
			sort: [
				{
					field: 'name',
					order: 'ASC',
				},
			],
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.searchInspectionTypes.items;
};

/**
 * Get inspection categories for a company
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Array>} List of inspection categories
 */
export const getInspectionCategories = async () => {
	const query = `
		query GetInspectionCategories($sort: [InspectionCategorySortInput]) {
			searchInspectionCategories(sort: $sort) {
				items {
					id
					name
					createdOn
					createdBy
					updatedOn
					updatedBy
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{
			sort: [
				{
					field: 'name',
					order: 'ASC',
				},
			],
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.searchInspectionCategories.items;
};

/**
 * Get inspection layouts for a company (entity lists for zone layouts)
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Array>} List of inspection layouts
 */
export const getInspectionLayouts = async () => {
	const query = `
		query GetInspectionLayouts($sort: [InspectionLayoutSortInput]) {
			searchInspectionLayouts(sort: $sort) {
				items {
					id
					name
					data {
						assetType
						assetViewGrid
						assetViewId
						configZones {
							tagComponents
							tag
							tagNumber
							tagType
							inspectionTypes
							assetViewLocation
						}
						components {
							component
							maxSeverity
							minSeverity
							conditions
						}
					}
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{
			sort: [
				{
					field: 'name',
					order: 'ASC',
				},
			],
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.searchInspectionLayouts.items;
};

/**
 * Get inspectors for a company (drivers)
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Array>} List of inspectors
 */
export const getInspectors = async () => {
	const query = `
		query GetInspectors {
			searchDrivers(limit: 1000) {
				items {
					id
					name
					empNum
					externalId
					status
					email
					phone
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.searchDrivers.items;
};

/**
 * Search inspectors by pattern
 * companyId is inferred by the backend from the user's auth token
 * @param {string} pattern - Search pattern
 * @returns {Promise<Array>} List of matching inspectors
 */
export const searchInspectors = async (pattern) => {
	const query = `
		query SearchInspectors($conditions: [DriverSearchInput], $sort: [DriverSortInput]) {
			searchDrivers(conditions: $conditions, sort: $sort, limit: 100) {
				items {
					id
					name
					title
					empNum
					externalId
					status
					email
					phone
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{
			conditions: [
				{
					fields: ['title'],
					terms: [pattern],
					exact: false,
				},
			],
			sort: [
				{
					field: 'title',
					order: 'ASC',
				},
			],
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.searchDrivers.items;
};

/**
 * Search assets by pattern
 * companyId is inferred by the backend from the user's auth token
 * @param {string} pattern - Search pattern
 * @returns {Promise<Array>} List of matching assets
 */
export const searchAssets = async (pattern) => {
	const query = `
		query SearchAssets($conditions: [AssetSearchInput], $sort: [AssetSortInput]) {
			searchAssets(conditions: $conditions, sort: $sort, limit: 100) {
				items {
					id
					label
					vin
					ecuVin
					licensePlate
					assetTypeId
					assetSubtypeId
					make
					model
					year
					isActive
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{
			conditions: [
				{
					fields: ['label'],
					terms: [pattern],
					exact: false,
				},
			],
			sort: [
				{
					field: 'label',
					order: 'ASC',
				},
			],
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	// Filter to active assets only (isActive is not a searchable field)
	const items = data.searchAssets.items || [];
	return items.filter((asset) => asset.isActive === true);
};

/**
 * Get inspection types mapping for a company
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Object>} Inspection types mapping
 */
export const getInspectionTypesMapping = async () => {
	// Note: This may need to be implemented as a custom query
	// or retrieved from searchInspectionTypes with additional fields
	const types = await getInspectionTypes();

	// Transform to mapping format expected by the UI
	return types.reduce((acc, type) => {
		acc[type.id] = type;
		return acc;
	}, {});
};

/**
 * Get config for a company
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Array>} List of configs
 */
export const getConfig = async () => {
	const query = `
		query GetPublishedConfigs {
			searchPublishedConfigs(limit: 100) {
				items {
					id
					sequence
					version
					configType
					data
					createdOn
					updatedOn
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.searchPublishedConfigs.items;
};

/**
 * Search/filter inspections with pagination
 * companyId is inferred by the backend from the user's auth token
 * @param {Object} params - Search parameters
 * @returns {Promise<Object>} Response with inspections data and total
 */
export const fetchInspections = async (params) => {
	const {
		page = 1,
		perPage = 25,
		sort = 'startTime',
		sortDir = 'desc',
	} = params;

	// Map UI sort field names to GraphQL Inspection field names
	const sortFieldMap = {
		startTime: 'startTime',
		severity: 'maxSeverity',
		inspectorName: 'driverId',
	};

	const graphqlSortField = sortFieldMap[sort] || 'startTime';

	const query = `
		query SearchInspections(
			$conditions: [InspectionSearchInput]
			$sort: [InspectionSortInput]
			$limit: Int
			$offset: Int
		) {
			searchInspections(
				conditions: $conditions
				sort: $sort
				limit: $limit
				offset: $offset
			) {
				items {
					id
					startTime
					typeId
					driver {
						name
					}
					maxSeverity
					type {
						id
						name
					}
					category {
						name
					}
					data
					failed
					failureReason
				}
				total
			}
		}
	`;

	const variables = {
		conditions: buildInspectionSearchConditions(params),
		sort: [{ field: graphqlSortField, order: sortDir.toUpperCase() }],
		limit: perPage,
		offset: (page - 1) * perPage,
	};

	try {
		const data = await clientQuery(
			query,
			variables,
			endpoints.GRAPHQL_GENERAL_V2
		);

		// Return format matching REST API
		return {
			data: data.searchInspections.items,
			headers: {
				'x-total-count': data.searchInspections.total,
			},
		};
	} catch (error) {
		console.error('[fetchInspections] Error:', error);
		throw error;
	}
};

/**
 * Get single inspection details
 * companyId is inferred by the backend from the user's auth token
 * @param {string} inspectionId - Inspection ID
 * @returns {Promise<Object>} Inspection details
 */
export const fetchInspectionDetails = async (inspectionId) => {
	const query = `
		query GetInspection($id: String!) {
			getInspection(id: $id) {
				id
				startTime
				driver {
					name
				}
				type {
					name
				}
				data
				failed
				failureReason
				inspectionDetails {
					inspectionTypeId
					inspectionTypeName
					inspectionCategoryId
					inspectionCategoryName
					inspectionDetailData {
						inspectionDetailDataName
						inspectionDetailDataType
						inspectionDetailCategory
						inspectionDetailContent
						inspectionDetailContentKey
						inspectionDetailIndex
						inspectionDetailMaxDecimal
						detailMedia {
							mediaId
							fileKey
							defectId
							thumbnail
						}
						telemetry {
							timestamp
							elevation
							horizontalAccuracy
							latitude
							longitude
						}
					}
				}
				inspectionAssets {
					assetSubTypeId
					assetSubtypeName
					assetCategory
					assetId
					assetName
					assetIsRental
					assetLicense
					assetMake
					assetModel
					assetVin
					zoneLayoutId
					zoneLayoutName
					assetViewGrid
					assetViewId
					inspectionZones {
						zoneName
						verificationType
						assetViewLocation
						sequence
						verificationData
						telemetry {
							timestamp
							elevation
							horizontalAccuracy
							latitude
							longitude
						}
						inspectionComponents {
						componentIndex
						componentName
						otherComponent
						inspectionDefects {
							id
							condition
							severity
							layoutId
							comment
							legacyDefectId
							legacyInspectionId
							defectMedia {
								mediaId
								fileKey
								defectId
								thumbnail
							}
						}
						}
					}
				}
				inspectionTimeInfo {
					start {
						timestamp
						elevation
						horizontalAccuracy
						latitude
						longitude
					}
					end {
						timestamp
						elevation
						horizontalAccuracy
						latitude
						longitude
					}
					certify {
						timestamp
						elevation
						horizontalAccuracy
						latitude
						longitude
					}
					upload {
						timestamp
						elevation
						horizontalAccuracy
						latitude
						longitude
					}
				}
				reviews {
					createdOn
					data {
						driverAction
						driverFirstName
						driverLastName
						comment
					}
				}
				repairs {
					createdOn
					data
				}
			}
		}
	`;

	const data = await clientQuery(
		query,
		{ id: inspectionId },
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.getInspection;
};

/**
 * Get the last inspection for an asset
 * Fetches recent inspections and filters client-side by assetId in the data JSON.
 * This is necessary because the GraphQL schema doesn't support direct assetId filtering
 * on the AssetInspection junction table.
 *
 * Note: REST API's /inspections/last has special filtering (requires_review=true, not rejected).
 * This function finds ALL matching inspections and prefers ones with more inspectionDetailData.
 *
 * companyId is inferred by the backend from the user's auth token
 * @param {number|string} assetId - PX Asset ID (integer)
 * @returns {Promise<Object>} Last inspection data
 */
export const getLastInspection = async (assetId) => {
	// Fetch recent inspections (last 180 days) and filter client-side by assetId
	// Extended to 180 days to catch older inspections that may have more detail data
	// The assetId is stored inside the data JSON field as inspectionAssets[].asset.assetId
	const endTime = new Date().toISOString();
	const startTime = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString();

	const query = `
		query SearchInspections(
			$conditions: [InspectionSearchInput]
			$sort: [InspectionSortInput]
			$limit: Int
		) {
			searchInspections(
				conditions: $conditions
				sort: $sort
				limit: $limit
			) {
				items {
					id
					startTime
					driver {
						name
					}
					name
					requiresReview
					data
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{
			conditions: [
				{
					range: {
						fields: ['startTime'],
						GTE: startTime,
						LTE: endTime,
					},
				},
			],
			sort: [{ field: 'startTime', order: 'DESC' }],
			limit: 1000, // Fetch more to find all matching this asset
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	const inspections = data.searchInspections?.items || [];
	console.log('[getLastInspection] Found', inspections.length, 'inspections in last 180 days for asset', assetId);

	// Find ALL inspections that include this asset
	const targetAssetId = Number(assetId);
	const matchingInspections = [];

	for (const inspection of inspections) {
		if (!inspection.data) continue;

		try {
			const inspectionData = typeof inspection.data === 'string'
				? JSON.parse(inspection.data)
				: inspection.data;

			// Check if any inspectionAsset has this assetId
			const hasAsset = inspectionData.inspectionAssets?.some(
				(ia) => ia.asset?.assetId === targetAssetId || Number(ia.asset?.assetId) === targetAssetId
			);

			if (hasAsset) {
				const detailData = inspectionData.inspectionDetail?.inspectionDetailData || [];
				matchingInspections.push({
					inspection,
					inspectionData,
					detailDataCount: detailData.length,
					startTime: inspection.startTime,
					requiresReview: inspection.requiresReview,
				});
			}
		} catch (e) {
			// Skip inspections with invalid JSON data
			continue;
		}
	}

	console.log('[getLastInspection] Found', matchingInspections.length, 'inspections for asset', assetId);

	if (matchingInspections.length === 0) {
		console.log('[getLastInspection] No inspection found for asset', assetId);
		return null;
	}

	// Log ALL matching inspections for debugging
	console.log('[getLastInspection] Matching inspections:');
	matchingInspections.forEach((match, idx) => {
		const categories = match.inspectionData.inspectionDetail?.inspectionDetailData?.map(
			(d) => d.inspectionDetailCategory
		) || [];
		console.log(`  [${idx}] id=${match.inspection.id}, startTime=${match.startTime}, detailDataCount=${match.detailDataCount}, requiresReview=${match.requiresReview}, categories=[${categories.join(', ')}]`);
	});

	// Strategy: Prefer inspections with requiresReview=true and more detailData
	// Sort by: requiresReview (true first), then detailDataCount (higher first), then startTime (newer first)
	matchingInspections.sort((a, b) => {
		// Prefer requiresReview = true (mimics REST API's /inspections/last filter)
		if (a.requiresReview && !b.requiresReview) return -1;
		if (!a.requiresReview && b.requiresReview) return 1;

		// Then prefer more detail data (indicates telemetry was captured)
		if (a.detailDataCount !== b.detailDataCount) {
			return b.detailDataCount - a.detailDataCount;
		}

		// Then prefer newer inspections
		return new Date(b.startTime) - new Date(a.startTime);
	});

	const best = matchingInspections[0];
	console.log('[getLastInspection] Selected inspection:', best.inspection.id,
		'with', best.detailDataCount, 'detail items, requiresReview=', best.requiresReview);

	// Log the detail data names for debugging
	if (best.inspectionData.inspectionDetail?.inspectionDetailData) {
		const names = best.inspectionData.inspectionDetail.inspectionDetailData.map(
			(d) => d.inspectionDetailDataName?.languageLabel || d.inspectionDetailCategory
		);
		console.log('[getLastInspection] Detail data fields:', names);
	}

	return best.inspection;
};

/**
 * Get missing inspections report
 * Note: This may require a custom query or computed field on the backend
 * For now, returning a placeholder structure
 * @param {Object} params - Search parameters
 * @returns {Promise<Object>} Response with missing inspections data
 */
export const getMissingInspections = async (params) => {
	// This would need to be implemented as a custom query on the GraphQL backend
	// or computed from existing inspection data on the client side
	console.warn('getMissingInspections: This requires backend implementation');

	return {
		data: [],
		headers: {
			'x-total-count': 0,
		},
	};
};

/**
 * Export inspections to CSV
 * companyId is inferred by the backend from the user's auth token
 * @param {Array<string>} inspectionIds - Array of inspection IDs to export
 * @param {Object} params - Export parameters
 * @returns {Promise<string>} Download URL or CSV payload
 */
export const downloadInspectionsCSV = async (params = {}) => {
	const {
		sort = 'startTime',
		sortDir = 'desc',
	} = params;

	const query = `
		query ExportInspections(
			$conditions: [InspectionSearchInput]
			$sort: [InspectionSortInput]
		) {
			exportInspections(
				conditions: $conditions
				sort: $sort
			)
		}
	`;

	const sortFieldMap = {
		startTime: 'startTime',
		severity: 'maxSeverity',
		inspectorName: 'driverId',
	};

	const conditions = buildInspectionSearchConditions(params);

	const variables = {
		conditions,
		sort: [
			{
				field: sortFieldMap[sort],
				order: sortDir.toUpperCase(),
			}
		],
	};

	const data = await clientQuery(
		query,
		variables,
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.exportInspections;
};

/**
 * Export inspections to PDF
 * Note: This requires a backend endpoint that returns PDF data
 * companyId is inferred by the backend from the user's auth token
 * @param {Array<string>} inspectionIds - Array of inspection IDs to export
 * @returns {Promise<void>} Downloads PDF file
 */
export const downloadInspectionsPDF = async (inspectionIds) => {
	console.warn('downloadInspectionsPDF: File exports typically require REST endpoint');
	// This would need to be kept as a REST endpoint or implemented via mutation
	// that returns a download URL
};

/**
 * Create an inspection review to certify/fix a failed inspection
 * This is used when fixing a failed inspection by confirming the driver
 * companyId is inferred by the backend from the user's auth token
 * @param {Object} reviewData - Review data
 * @param {string} reviewData.inspectionId - The inspection ID to review
 * @param {string} reviewData.driverFirstName - Driver's first name
 * @param {string} reviewData.driverLastName - Driver's last name
 * @param {string} reviewData.driverAction - CERTIFIED or REJECTED
 * @param {string} reviewData.comment - Optional comment
 * @returns {Promise<Object>} Created review data
 */
export const createInspectionReview = async (reviewData) => {
	const mutation = `
		mutation CreateInspectionReview($id: ID!, $data: ReviewInfoInput!) {
			createInspectionReview(id: $id, data: $data) {
				id
				inspectionId
				driverAction
				data
				createdOn
				createdBy
				updatedOn
				updatedBy
			}
		}
	`;

	const reviewId = `review_${reviewData.inspectionId}_${Date.now()}`;

	const data = await clientMutation(
		mutation,
		{
			id: reviewId,
			data: {
				inspectionId: reviewData.inspectionId,
				driverAction: reviewData.driverAction || 'CERTIFIED',
				driverFirstName: reviewData.driverFirstName,
				driverLastName: reviewData.driverLastName,
				comment: reviewData.comment,
				draft: false,
			},
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.createInspectionReview;
};
